import { computed, ref } from 'vue'
import axios from 'axios'
import { Capacitor } from '@capacitor/core'
import { version as localVersion } from '../../package.json'
import tools from '@/composables/tools'
import { hardReloadWebApp } from '@/utils/deployVersion'

export const APP_VERSION_SLUG = 'cli'

export type AccionActualizar = 'recargar' | 'store' | string

export interface FrontVersionInfo {
  app: string
  version_latest: string | null
  version_web?: string | null
  version_min: string | null
  url_actualizacion: string | null
  store_url_ios: string | null
  store_url_android: string | null
  store_android_lista?: boolean | null
  store_android_disponible_desde?: string | null
  store_ios_lista?: boolean | null
  accion_actualizar?: AccionActualizar | null
  evento_tracking?: string | Record<string, unknown> | null
  mensaje: string | null
  activo: boolean
  version_cliente: string | null
  estado: 'ok' | 'aviso' | 'forzar' | null
  force: boolean
  aviso: boolean
}

const POLL_MS = 10 * 60 * 1000
const SNOOZE_PREFIX = 'app_version_snooze:'

const isOpen = ref(false)
const info = ref<FrontVersionInfo | null>(null)
const presentedFor = ref<string | null>(null)
let pollTimer: ReturnType<typeof setInterval> | null = null
let started = false
let updating = false

function snoozeKey(latest: string): string {
  return SNOOZE_PREFIX + latest
}

function isSnoozed(latest: string | null): boolean {
  if (!latest) {
    return false
  }
  try {
    return sessionStorage.getItem(snoozeKey(latest)) === '1'
  } catch {
    return false
  }
}

function setSnoozed(latest: string | null): void {
  if (!latest) {
    return
  }
  try {
    sessionStorage.setItem(snoozeKey(latest), '1')
  } catch {
    /* noop */
  }
}

function whatsappPhone(): string {
  return String(process.env.VUE_APP_WHATSAPP_HELP || '+34604048881').replace(/[^\d+]/g, '')
}

function isNativeAndroid(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android'
}

function isNativeIos(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios'
}

/** Play ya tiene build publicada (o la API pide ir a la store). */
function androidStoreReady(row: FrontVersionInfo): boolean {
  if (row.accion_actualizar === 'store') {
    return Boolean(row.store_url_android)
  }
  if (row.store_android_lista === true) {
    return Boolean(row.store_url_android)
  }
  // Sin campo lista: no asumir lista (evita abrir Play vacío)
  if (row.store_android_lista === false) {
    return false
  }
  return false
}

function iosStoreReady(row: FrontVersionInfo): boolean {
  if (row.accion_actualizar === 'store' && row.store_url_ios) {
    return true
  }
  if (row.store_ios_lista === true && row.store_url_ios) {
    return true
  }
  return false
}

/**
 * Aviso inútil en Android nativo: Play aún no tiene la build.
 * La API debería mandar aviso:false; si llega aviso sin store lista, no molestar (salvo force).
 */
function shouldSuppressAndroidStoreAviso(row: FrontVersionInfo): boolean {
  if (!isNativeAndroid() || !row.aviso) {
    return false
  }
  if (row.force) {
    return false
  }
  if (row.accion_actualizar === 'recargar') {
    return false
  }
  return !androidStoreReady(row)
}

function logAccion(accion: 'mostrado' | 'actualizar' | 'mas_tarde' | 'omitido' | 'whatsapp'): void {
  const row = info.value
  const latest = row?.version_latest ?? '?'
  const estado = row?.estado ?? '?'
  const et = row?.evento_tracking

  let eventMsg: string
  if (typeof et === 'string' && et.trim()) {
    eventMsg = `${et.trim()} · ${accion} · local=${localVersion} · latest=${latest} · estado=${estado}`
  } else if (et && typeof et === 'object') {
    const base = String(
      (et as { event?: unknown; evento?: unknown }).event
      ?? (et as { evento?: unknown }).evento
      ?? 'Aviso versión desactualizada'
    )
    eventMsg = `${base}: ${accion} · local=${localVersion} · latest=${latest} · estado=${estado}`
  } else {
    eventMsg = `Aviso versión desactualizada: ${accion} · local=${localVersion} · latest=${latest} · estado=${estado}`
  }

  tools().setLog(eventMsg, 'info')
}

/** Abre URL de tienda en WebView Capacitor (window.open suele bloquearse). */
function openExternalStoreUrl(url: string): void {
  try {
    const a = document.createElement('a')
    a.href = url
    a.target = '_blank'
    a.rel = 'noopener noreferrer'
    document.body.appendChild(a)
    a.click()
    a.remove()
  } catch {
    /* noop */
  }
  // Fallback fiable en WebView Android
  window.setTimeout(() => {
    window.location.href = url
  }, 250)
}

export function useAppVersionCheck() {
  const force = computed(() => Boolean(info.value?.force))

  const mensaje = computed(() => {
    const custom = info.value?.mensaje?.trim()
    if (custom) {
      return custom
    }
    const latest = info.value?.version_latest ?? ''
    return latest
      ? `Estás usando la versión ${localVersion} y hay una más reciente (${latest}). Debes actualizar la aplicación para seguir usándola con normalidad.`
      : `Estás usando la versión ${localVersion}, que ya no está al día. Debes actualizar la aplicación.`
  })

  async function check(): Promise<void> {
    try {
      // Bearer si hay sesión: axios.defaults ya lleva Authorization
      const response = await axios.get(
        `/front/versiones/${APP_VERSION_SLUG}`,
        {
          params: { version: localVersion },
          skipErrorToast: true,
        }
      )
      const data = response.data as FrontVersionInfo

      if (!data?.activo || !data.aviso) {
        isOpen.value = false
        info.value = data ?? null
        return
      }

      info.value = data

      if (shouldSuppressAndroidStoreAviso(data)) {
        isOpen.value = false
        return
      }

      if (!data.force && isSnoozed(data.version_latest)) {
        isOpen.value = false
        return
      }

      const firma = `${data.estado}:${data.version_latest}:${localVersion}:${data.accion_actualizar ?? ''}`
      const yaPresentado = presentedFor.value === firma
      isOpen.value = true
      if (!yaPresentado) {
        presentedFor.value = firma
        logAccion('mostrado')
      }
    } catch {
      /* catálogo no disponible: no molestar */
    }
  }

  function start(): void {
    if (started) {
      return
    }
    started = true
    void check()
    pollTimer = setInterval(() => {
      void check()
    }, POLL_MS)

    document.addEventListener('visibilitychange', onVisibility)
  }

  function onVisibility(): void {
    if (document.visibilityState === 'visible') {
      void check()
    }
  }

  function stop(): void {
    if (pollTimer) {
      clearInterval(pollTimer)
      pollTimer = null
    }
    document.removeEventListener('visibilitychange', onVisibility)
    started = false
  }

  /**
   * Decide acción del botón Actualizar:
   * - navegador/PWA / accion=recargar → hard reload
   * - Android nativo + store lista → abrir Play
   * - iOS nativo + store lista → App Store
   * - iOS sin señal de store → recargar (WebView casi sin wv)
   */
  async function onActualizar(): Promise<void> {
    if (updating) {
      return
    }
    updating = true
    try {
      logAccion('actualizar')
      const row = info.value
      if (!row) {
        await hardReloadWebApp()
        return
      }

      const accion = (row.accion_actualizar || '').toLowerCase()

      // A) Recarga web / PWA / orden explícita de API
      if (
        accion === 'recargar'
        || !Capacitor.isNativePlatform()
        || (isNativeIos() && !iosStoreReady(row))
      ) {
        await hardReloadWebApp()
        return
      }

      // B) Android nativo → Play solo si la store está lista
      if (isNativeAndroid()) {
        if (androidStoreReady(row) && row.store_url_android) {
          openExternalStoreUrl(row.store_url_android)
          return
        }
        logAccion('omitido')
        if (!force.value) {
          isOpen.value = false
        }
        return
      }

      // C) iOS nativo con store lista
      if (isNativeIos() && iosStoreReady(row) && row.store_url_ios) {
        openExternalStoreUrl(row.store_url_ios)
        return
      }

      // Sin URL de tienda resoluble: no abrir Play vacío ni bucle
      logAccion('omitido')
      if (!force.value) {
        isOpen.value = false
      } else {
        await hardReloadWebApp()
      }
    } finally {
      updating = false
    }
  }

  function onWhatsapp(): void {
    logAccion('whatsapp')
    const latest = info.value?.version_latest ?? ''
    const text = encodeURIComponent(
      `Hola, estoy en la app Galidancia versión ${localVersion}`
      + (latest ? ` y me pide actualizar a ${latest}` : ' y me indica que está desactualizada')
      + '. Necesito asistencia técnica para actualizar.'
    )
    const phone = whatsappPhone()
    const digits = phone.replace(/^\+/, '')
    window.location.href = `whatsapp://send?phone=${phone}&text=${text}`
    window.setTimeout(() => {
      window.open(`https://wa.me/${digits}?text=${text}`, '_blank', 'noopener,noreferrer')
    }, 600)
  }

  function onMasTarde(): void {
    if (force.value) {
      return
    }
    logAccion('mas_tarde')
    setSnoozed(info.value?.version_latest ?? null)
    isOpen.value = false
  }

  return {
    isOpen,
    info,
    force,
    mensaje,
    localVersion,
    start,
    stop,
    onActualizar,
    onWhatsapp,
    onMasTarde,
  }
}
